import { useId, type CSSProperties } from "react";
import type { ApiCommerceOffer } from "@bridge-applications/pb-types";
import { Trans, useTranslation } from "react-i18next";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import { ButtonVar1 } from "../../../../common/ButtonVar1/ButtonVar1";
import {
    energyAmountForOffer,
    formatCommercePrice,
    formatIntegerAmount,
    telegramStarsPrice,
} from "../../../../../logic/helpers/commerce";
import {
    keepPlayingCountdown,
    type KeepPlayingRefill,
} from "../../../../../logic/helpers/keepPlayingOffer";
import { resolveSupportedLocale } from "../../../../../logic/i18n/locales";
import energySplash from "../../../../../assets/static/images/commerce/keep-playing/energy-casn-splash-img.webp";
import TelegramStar from "../../../../../assets/static/images/commerce/keep-playing/tg-star-green-outline.svg?react";
import "./KeepPlayingOfferDialog.scss";

const titleAssets = import.meta.glob<string>(
    "../../../../../assets/static/images/commerce/keep-playing/titles/*.webp",
    { eager: true, import: "default" },
);
const titlePrefix =
    "../../../../../assets/static/images/commerce/keep-playing/titles/header-title-img__keep-playing__";

interface KeepPlayingOfferDialogProps {
    isOpen: boolean;
    offer: ApiCommerceOffer;
    refill: KeepPlayingRefill | null;
    availability: "available" | "expired" | "unavailable";
    isBusy: boolean;
    error: string | null;
    onClose: () => void;
    onPurchase: () => void;
}

export function KeepPlayingOfferDialog({
    isOpen,
    offer,
    refill,
    availability,
    isBusy,
    error,
    onClose,
    onPurchase,
}: KeepPlayingOfferDialogProps) {
    const { t, i18n } = useTranslation();
    const locale = resolveSupportedLocale(i18n.resolvedLanguage, i18n.language);
    const titleId = useId();
    const titleGradientId = useId();
    const energy = energyAmountForOffer(offer);
    const amount = energy ? formatIntegerAmount(energy, locale) : null;
    const price = telegramStarsPrice(offer);
    const priceAmount = price
        ? formatIntegerAmount(price.unitAmount, locale)
        : null;
    const titleSrc =
        titleAssets[`${titlePrefix}${locale}.webp`] ??
        titleAssets[`${titlePrefix}en.webp`];
    const unavailable = availability !== "available" || !price || !energy;
    const statusMessage =
        error ??
        (availability === "expired" ? t("commerce.chain.expired") : null);
    const buttonLabel = isBusy
        ? t("commerce.checkout.processing")
        : unavailable
          ? t("commerce.chain.unavailable")
          : null;
    const visualStyle = {
        "--keep-playing-amount-size": Math.min(
            48.75,
            290 / Math.max(1, (amount?.length ?? 0) + 1),
        ),
        "--keep-playing-price-size": Math.min(
            36.1179,
            150 / Math.max(1, priceAmount?.length ?? 1),
        ),
    } as CSSProperties;

    return (
        <SimpleDialog
            className="keep-playing-offer-dialog"
            isOpen={isOpen}
            onClose={onClose}
            aria-labelledby={titleId}
        >
            <section
                className="keep-playing-offer-dialog__panel"
                style={visualStyle}
                aria-busy={isBusy}
            >
                <div
                    className="keep-playing-offer-dialog__rim"
                    aria-hidden="true"
                />
                <h2 className="keep-playing-offer-dialog__title" id={titleId}>
                    {locale === "zh-Hans" ? (
                        // The supplied zh-Hans bitmap contains Traditional
                        // characters. Use the localized copy until corrected art exists.
                        <svg
                            className="keep-playing-offer-dialog__text-title"
                            viewBox="0 0 306 64"
                            role="img"
                            aria-label={t("commerce.keepPlaying.title")}
                        >
                            <defs>
                                <linearGradient
                                    id={titleGradientId}
                                    x2="0"
                                    y2="1"
                                >
                                    <stop offset="0" stopColor="#39b3e8" />
                                    <stop offset="0.48" stopColor="#99edfc" />
                                    <stop offset="0.52" stopColor="#0cc8f1" />
                                    <stop offset="1" stopColor="#168dc9" />
                                </linearGradient>
                            </defs>
                            <text
                                x="153"
                                y="52"
                                fill="#0873a8"
                                stroke="#063d64"
                                strokeWidth="4"
                            >
                                {t("commerce.keepPlaying.title")}
                            </text>
                            <text
                                x="153"
                                y="48"
                                fill={`url(#${titleGradientId})`}
                                stroke="#063d64"
                                strokeWidth="2.5"
                            >
                                {t("commerce.keepPlaying.title")}
                            </text>
                        </svg>
                    ) : (
                        <img
                            src={titleSrc}
                            alt={t("commerce.keepPlaying.title")}
                            draggable={false}
                        />
                    )}
                </h2>
                <div className="keep-playing-offer-dialog__reward">
                    <img
                        className="keep-playing-offer-dialog__splash"
                        src={energySplash}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        width={780}
                        height={649}
                    />
                    <p
                        className="keep-playing-offer-dialog__amount"
                        aria-label={
                            energy
                                ? t("commerce.rewards.energy", { amount })
                                : t("commerce.chain.unavailable")
                        }
                        dir="ltr"
                    >
                        {amount === null ? "—" : `+${amount}`}
                    </p>
                </div>
                <ButtonVar1
                    className="keep-playing-offer-dialog__purchase"
                    disabled={isBusy || unavailable}
                    onClick={onPurchase}
                    aria-label={
                        buttonLabel ??
                        t("commerce.energy.purchaseAriaLabel", {
                            amount,
                            price: price
                                ? formatCommercePrice(price, locale)
                                : "",
                        })
                    }
                    colors={{
                        containerStroke: "#275014",
                        containerFill: "linear-gradient(#397513 83%, #355c27)",
                        faceStroke:
                            "linear-gradient(#aad756, #86bf30 42%, #69a43a)",
                        faceFill: "linear-gradient(#a5d91c, #509f12)",
                    }}
                    content={
                        buttonLabel ? (
                            <span className="keep-playing-offer-dialog__purchase-status">
                                {buttonLabel}
                            </span>
                        ) : (
                            <span
                                className="keep-playing-offer-dialog__purchase-price"
                                dir="ltr"
                                aria-hidden="true"
                            >
                                <TelegramStar />
                                <span>{priceAmount}</span>
                            </span>
                        )
                    }
                />
                {statusMessage && (
                    <p
                        className="keep-playing-offer-dialog__error"
                        role="alert"
                    >
                        {statusMessage}
                    </p>
                )}
                {refill && (
                    <p className="keep-playing-offer-dialog__wait">
                        {refill.secondsRemaining === 0 ? (
                            t("commerce.keepPlaying.refreshing")
                        ) : (
                            <Trans
                                i18nKey="commerce.keepPlaying.wait"
                                count={Number(refill.spins)}
                                values={{
                                    time: keepPlayingCountdown(
                                        refill.secondsRemaining,
                                    ),
                                    spins: formatIntegerAmount(
                                        refill.spins,
                                        locale,
                                    ),
                                }}
                                components={{
                                    time: <bdi dir="ltr" />,
                                    spins: <bdi />,
                                }}
                            />
                        )}
                    </p>
                )}
            </section>
        </SimpleDialog>
    );
}
